import { isValidElement, type ReactNode, type ComponentProps } from "react";
import { Link, useLocation } from "react-router";
import { ButtonLink } from "@zylarity/ui";

/** Page context only: never retain a referrer, token, arbitrary query or visitor identity. */
export function inquiryOrigin(path: string | null): string | undefined {
  if (!path || path.length > 300) return undefined;
  return /^\/(?:employers(?:\/(?:talent-assessments|secure-job-descriptions))?|professionals(?:\/career-snapshot)?|our-process(?:\/examples)?|intelligence(?:\/[a-z0-9-]+)?|about|match-analysis|jobs(?:\/[a-z0-9-]+)?|recruitment-safety|privacy)?$/u.test(
    path,
  )
    ? path
    : undefined;
}
export function inquiryService(
  params: URLSearchParams,
): "secure-job-descriptions" | "talent-assessments" | undefined {
  const service = params.get("service");
  if (service === "secure-job-descriptions" || service === "talent-assessments") return service;
  return params.get("kind") === "ASSESSMENT" ? "talent-assessments" : undefined;
}
export function withInquiryOrigin(
  href: string,
  pathname: string,
  entryLink = "contact-link",
): string {
  if (!/^\/(?:contact|intake\/(?:candidate|client))(?:[?#]|$)/u.test(href)) return href;
  const url = new URL(href, "https://zylarity.com");
  if (url.searchParams.has("profile")) return href;
  const service = inquiryService(url.searchParams);
  if (service) url.searchParams.set("service", service);
  else url.searchParams.delete("service");
  if (url.pathname === "/contact") {
    const kind = url.searchParams.get("kind");
    if (kind && ["EMPLOYER", "ASSESSMENT", "PROFESSIONAL"].includes(kind)) {
      url.pathname = kind === "PROFESSIONAL" ? "/intake/candidate" : "/intake/client";
      url.searchParams.delete("kind");
      url.searchParams.set("intro", "1");
    }
  } else if (
    !url.searchParams.has("publicJobId") &&
    !url.searchParams.has("interest") &&
    !url.searchParams.has("profile")
  ) {
    url.searchParams.set("intro", "1");
  }
  const origin = inquiryOrigin(url.searchParams.get("from")) ?? inquiryOrigin(pathname);
  if (origin) url.searchParams.set("from", origin);
  else url.searchParams.delete("from");
  url.searchParams.set("via", inquiryEntryLink(url.searchParams.get("via")) ?? entryLink);
  return `${url.pathname}${url.search}${url.hash}`;
}
function linkText(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(linkText).join(" ");
  if (isValidElement<{ children?: ReactNode }>(children)) return linkText(children.props.children);
  return "";
}
export function inquiryEntryLink(value: string | null): string | undefined {
  return value && /^[a-z0-9]+(?:-[a-z0-9]+)*$/u.test(value) && value.length <= 96
    ? value
    : undefined;
}
function entryLabel(children: ReactNode) {
  return (
    linkText(children)
      .toLowerCase()
      .replace(/[^a-z0-9]+/gu, "-")
      .replace(/^-|-$/gu, "")
      .slice(0, 96)
      .replace(/-$/u, "") || "contact-link"
  );
}
export function InquiryButtonLink({
  via,
  ...props
}: ComponentProps<typeof ButtonLink> & { via?: string }) {
  const { pathname } = useLocation();
  return (
    <ButtonLink
      {...props}
      href={withInquiryOrigin(props.href, pathname, via ?? entryLabel(props.children))}
    />
  );
}
export function InquiryLink({ via, ...props }: ComponentProps<typeof Link> & { via?: string }) {
  const { pathname } = useLocation();
  return (
    <Link
      {...props}
      to={
        typeof props.to === "string"
          ? withInquiryOrigin(
              props.to,
              pathname,
              via ?? entryLabel(typeof props.children === "function" ? undefined : props.children),
            )
          : props.to
      }
    />
  );
}
